var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import styles from './styles.module.scss';
import CloseIcon from '../../icons/close-icon.svg';
import { HeadingS, TextS } from '../typography/typography';
import { withAliases } from '../../../ui/utils/with-aliases';
import { useClickOutside } from '../../../small-tools/use-click-outside';
import { IconButton } from '../icon-button/icon-button';
export var MODAL_ANIMATION_DURATION = 200;
var Actions = function (_a) {
    var children = _a.children;
    return (_jsx("div", { className: styles['actions-container'], children: _jsx("div", { className: styles['modal-actions'], children: children }) }));
};
var Content = function (_a) {
    var children = _a.children;
    return _jsx("div", { className: styles.content, children: children });
};
var BaseModal = function (_a) {
    var _b, _c;
    var open = _a.open, onClose = _a.onClose, subtitle = _a.subtitle, children = _a.children, title = _a.title, className = _a.className, disabled = _a.disabled, closeOnOutsideClick = _a.closeOnOutsideClick, rest = __rest(_a, ["open", "onClose", "subtitle", "children", "title", "className", "disabled", "closeOnOutsideClick"]);
    var modalRef = useRef(null);
    var _d = useState(false), isClosing = _d[0], setIsClosing = _d[1];
    var _e = useState(false), shouldRender = _e[0], setShouldRender = _e[1];
    var handleClose = useCallback(function () {
        setIsClosing(true);
        setTimeout(function () {
            onClose();
        }, MODAL_ANIMATION_DURATION);
    }, [onClose]);
    var handleClickOutside = useCallback(function () {
        if (closeOnOutsideClick) {
            handleClose();
        }
    }, [closeOnOutsideClick, handleClose]);
    useClickOutside(modalRef, handleClickOutside, ['data-modal']);
    useEffect(function () {
        var timer;
        var scrollY = window.scrollY;
        if (open) {
            setIsClosing(false);
            setShouldRender(true);
            // Lock scroll
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.top = "-".concat(scrollY, "px");
            document.body.style.overflow = 'hidden';
        }
        else {
            setIsClosing(true);
            timer = setTimeout(function () {
                setShouldRender(false);
            }, MODAL_ANIMATION_DURATION);
        }
        return function () {
            if (timer) {
                clearTimeout(timer);
            }
            document.body.style.position = 'relative';
            document.body.style.width = 'auto';
            document.body.style.overflow = '';
            document.body.style.top = '';
            if (scrollY) {
                window.scrollTo(0, scrollY);
            }
        };
    }, [open]);
    // Handle escape key
    useEffect(function () {
        var handleEscapeKey = function (event) {
            if (event.key === 'Escape') {
                handleClose();
            }
        };
        document.addEventListener('keydown', handleEscapeKey);
        return function () {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [handleClose]);
    if (!shouldRender)
        return null;
    var actionsElement = React.Children.toArray(children).find(function (child) { return React.isValidElement(child) && child.type === Actions; });
    var contentElement = React.Children.toArray(children).find(function (child) { return React.isValidElement(child) && child.type === Content; }) || children;
    return ReactDOM.createPortal(_jsxs("div", { className: classNames(className, styles['modal-container']), children: [_jsx("div", { className: classNames(styles.backdrop, (_b = {},
                    _b[styles.closing] = isClosing,
                    _b)) }), _jsxs("div", __assign({ ref: modalRef, className: classNames(styles.modal, (_c = {},
                    _c[styles.closing] = isClosing,
                    _c)), "data-modal": true }, rest, { children: [_jsxs("div", { className: styles['header-container'], children: [_jsxs("div", { className: styles['header-title-container'], children: [_jsx(HeadingS, { color: disabled ? 'disabled' : 'primary', children: title }), subtitle && _jsx(TextS, { color: disabled ? 'disabled' : 'secondary', children: subtitle })] }), _jsx(IconButton.L, { Icon: _jsx(CloseIcon, {}), onClick: handleClose, disabled: disabled, "data-testid": "modal-close" })] }), contentElement, actionsElement] }))] }), document.body);
};
export var Modal = withAliases(BaseModal, {
    Actions: Actions,
    Content: Content,
});
